/**
 * Action: tag_household  (+ inverse: untag_household)
 *
 * Apply / remove a single tag to/from a household. Same polymorphic
 * `taggings` pattern as tag_person / tag_group; taggable_type is
 * "Household". Pairs with the focused-household context (#1784).
 */

import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { logAudit } from "@/lib/audit";
import { registerAction } from "../registry";
import type { ActionDefinition, ActionResult } from "../types";

const tagHouseholdParams = z.object({
  household_id: z.string().guid(),
  tag_id: z.string().guid(),
});
type TagHouseholdParams = z.infer<typeof tagHouseholdParams>;

async function resolveHouseholdAndTag(
  organizationId: string,
  householdId: string,
  tagId: string,
): Promise<{ householdName: string; tagName: string }> {
  const [hhResult, tagResult] = await Promise.all([
    supabase
      .from("households")
      .select("id, name")
      .eq("organization_id", organizationId)
      .eq("id", householdId)
      .maybeSingle(),
    supabase
      .from("tags")
      .select("id, name")
      .eq("organization_id", organizationId)
      .eq("id", tagId)
      .maybeSingle(),
  ]);

  if (hhResult.error || !hhResult.data) {
    throw new Error("Household not found in this organization.");
  }
  if (tagResult.error || !tagResult.data) {
    throw new Error("Tag not found in this organization.");
  }
  return {
    householdName: (hhResult.data.name ?? "").trim() || "this household",
    tagName: tagResult.data.name,
  };
}

const tagHouseholdDefinition: ActionDefinition<TagHouseholdParams> = {
  id: "tag_household",
  label: "Tag household",
  description: "Apply a single tag to a household.",
  requiredTier: "staff",
  paramsSchema: tagHouseholdParams,
  defaultRequiresConfirm: false,
  async buildPreview(params, ctx) {
    const { householdName, tagName } = await resolveHouseholdAndTag(
      ctx.organizationId,
      params.household_id,
      params.tag_id,
    );
    return { summary: `Tag ${householdName} with "${tagName}"` };
  },
  async execute(params, ctx): Promise<ActionResult> {
    const { householdName, tagName } = await resolveHouseholdAndTag(
      ctx.organizationId,
      params.household_id,
      params.tag_id,
    );

    const { error } = await supabase.from("taggings").upsert(
      {
        organization_id: ctx.organizationId,
        tag_id: params.tag_id,
        taggable_type: "Household",
        taggable_id: params.household_id,
      },
      { onConflict: "organization_id,tag_id,taggable_type,taggable_id" },
    );

    if (error) {
      return {
        ok: false,
        summary: `Failed to tag ${householdName}`,
        error: error.message,
      };
    }

    const audit = await logAudit({
      action: "tag.apply",
      tableName: "taggings",
      recordId: params.household_id,
      organizationId: ctx.organizationId,
      userId: ctx.userId,
      newData: {
        tag_id: params.tag_id,
        tag_name: tagName,
        household_id: params.household_id,
        taggable_type: "Household",
        source: ctx.source,
        reason: ctx.reason,
      },
    });

    return {
      ok: true,
      summary: `Tagged ${householdName} with "${tagName}"`,
      auditLogId: audit?.id ?? null,
      inverse: {
        actionId: "untag_household",
        params: { household_id: params.household_id, tag_id: params.tag_id },
        label: `Undo: remove "${tagName}"`,
      },
    };
  },
};

const untagHouseholdDefinition: ActionDefinition<TagHouseholdParams> = {
  id: "untag_household",
  label: "Remove tag from household",
  description: "Remove a single tag from a household.",
  requiredTier: "staff",
  paramsSchema: tagHouseholdParams,
  defaultRequiresConfirm: false,
  async buildPreview(params, ctx) {
    const { householdName, tagName } = await resolveHouseholdAndTag(
      ctx.organizationId,
      params.household_id,
      params.tag_id,
    );
    return { summary: `Remove "${tagName}" from ${householdName}` };
  },
  async execute(params, ctx): Promise<ActionResult> {
    const { householdName, tagName } = await resolveHouseholdAndTag(
      ctx.organizationId,
      params.household_id,
      params.tag_id,
    );

    const { error } = await supabase
      .from("taggings")
      .delete()
      .eq("organization_id", ctx.organizationId)
      .eq("tag_id", params.tag_id)
      .eq("taggable_type", "Household")
      .eq("taggable_id", params.household_id);

    if (error) {
      return {
        ok: false,
        summary: `Failed to remove tag from ${householdName}`,
        error: error.message,
      };
    }

    const audit = await logAudit({
      action: "tag.remove",
      tableName: "taggings",
      recordId: params.household_id,
      organizationId: ctx.organizationId,
      userId: ctx.userId,
      oldData: {
        tag_id: params.tag_id,
        tag_name: tagName,
        household_id: params.household_id,
      },
      newData: { taggable_type: "Household", source: ctx.source, reason: ctx.reason },
    });

    return {
      ok: true,
      summary: `Removed "${tagName}" from ${householdName}`,
      auditLogId: audit?.id ?? null,
      inverse: {
        actionId: "tag_household",
        params: { household_id: params.household_id, tag_id: params.tag_id },
        label: `Undo: re-apply "${tagName}"`,
      },
    };
  },
};

registerAction(tagHouseholdDefinition);
registerAction(untagHouseholdDefinition);

export { tagHouseholdDefinition, untagHouseholdDefinition };
